/**
 * "Marketplace fees you avoided" — value-story card for the dashboard's right
 * column. Neutral surface, green as an accent. Sections: hero + ROI-vs-plan,
 * a 6-month trend sparkline + projection, quick stats, channel mix, an untapped
 * nudge, and top categories. Fails quiet so it never breaks the dashboard.
 */
import { PiggyBank, Info, Sparkles, ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import { ShopeeIcon, LazadaIcon, TikTokIcon } from "@/features/channels/components/ChannelBrandIcons";
import { useMarketplaceSavings } from "../hooks/useMarketplaceSavings";
import { useGetTenantUsage } from "@/hooks/useGetTenantUsage";

function money(n: number, currency: string, max = 0) {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: currency || "THB",
    maximumFractionDigits: max,
  }).format(n || 0);
}

/** Tiny inline SVG sparkline (no chart library). */
function Sparkline({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const W = 100;
  const H = 28;
  const step = W / (points.length - 1);
  const coords = points.map((p, i) => [i * step, H - ((p - min) / range) * (H - 4) - 2] as const);
  const line = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-8 text-emerald-500">
      <polygon points={`0,${H} ${line} ${W},${H}`} fill="url(#spk)" />
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="2" fill="currentColor" />
      <defs>
        <linearGradient id="spk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function MarketplaceSavingsCard() {
  const { data, isLoading, isError } = useMarketplaceSavings();
  const { data: usage } = useGetTenantUsage();

  if (isError) return null;

  if (isLoading) {
    return <div className="rounded-xl border border-border bg-white dark:bg-white/[0.02] p-5 h-[420px] animate-pulse" />;
  }

  const cur = data?.currency || "THB";
  const savedShopee = data?.all_time.saved_vs_shopee ?? 0;
  const savedLazada = data?.all_time.saved_vs_lazada ?? 0;
  const orders = data?.all_time.direct_orders ?? 0;
  const monthShopee = data?.this_month.saved_vs_shopee ?? 0;
  const topCats = data?.all_time.by_category ?? [];
  const trend = data?.monthly_trend ?? [];
  const mix = data?.channel_mix;
  const feesPaid = data?.marketplace_fees_paid;
  const untapped = data?.untapped_saved ?? 0;
  const daysElapsed = data?.month_days_elapsed ?? 1;
  const daysTotal = data?.month_days_total ?? 30;

  if (savedShopee <= 0) return null;

  // Derived
  const perOrder = orders > 0 ? savedShopee / orders : 0;
  const projected = daysElapsed > 0 ? (monthShopee / daysElapsed) * daysTotal : monthShopee;
  const planCost = (usage as { estimated_monthly_total_thb?: number } | undefined)?.estimated_monthly_total_thb ?? 0;
  const roiMonths = planCost > 0 ? Math.round(savedShopee / planCost) : 0;

  const milestones = [5_000_000, 1_000_000, 500_000, 100_000];
  const milestone = milestones.find((m) => savedShopee >= m);

  // The trend's last point is the CURRENT month (still partial) — exclude it from
  // rate/delta math so an incomplete month never reads as a real drop.
  const completeTrend = trend.slice(0, -1);

  // #2 Annualized run-rate — avg of the last ≤3 non-zero COMPLETE months × 12 (fallback: this-month projection)
  const recentMonths = completeTrend.slice(-3).map((t) => t.saved).filter((v) => v > 0);
  const monthlyRunRate = recentMonths.length
    ? recentMonths.reduce((a, b) => a + b, 0) / recentMonths.length
    : projected;
  const annualized = monthlyRunRate * 12;

  // #3 Month-over-month delta between the two most recent COMPLETE months
  const lastSaved = completeTrend.length ? completeTrend[completeTrend.length - 1].saved : 0;
  const prevSaved = completeTrend.length > 1 ? completeTrend[completeTrend.length - 2].saved : 0;
  const deltaPct = prevSaved > 0 ? Math.round(((lastSaved - prevSaved) / prevSaved) * 100) : null;

  // Channel mix percentages
  const total = mix?.total || 0;
  const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
  const directPct = pct(mix?.direct ?? 0);
  const shopeePct = pct(mix?.shopee ?? 0);
  const lazadaPct = pct(mix?.lazada ?? 0);
  const otherPct = Math.max(0, 100 - directPct - shopeePct - lazadaPct);

  return (
    <div className="rounded-xl border border-border bg-white dark:bg-white/[0.02] p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
            <PiggyBank className="w-[18px] h-[18px]" strokeWidth={1.75} />
          </span>
          <h3 className="text-sm font-semibold text-foreground">ค่าธรรมเนียมที่ประหยัดได้</h3>
        </div>
        {milestone && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-300 bg-amber-500/10 rounded-full px-2 py-0.5 shrink-0">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
            ฿{(milestone / 1_000_000 >= 1 ? `${milestone / 1_000_000}M` : `${milestone / 1000}k`)}+
          </span>
        )}
      </div>

      {/* Hero + ROI */}
      <div className="mt-5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[32px] leading-none font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
            {money(savedShopee, cur)}
          </span>
          <span className="inline-flex items-center gap-1 leading-none text-[11px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/[0.06] rounded-full px-2 py-0.5">
            <ShopeeIcon className="w-3 h-3 shrink-0" />
            <span className="leading-none">เทียบกับ Shopee</span>
          </span>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          ค่าคอมมิชชันที่เลี่ยงได้จากการขายตรง{" "}
          <span className="whitespace-nowrap font-medium text-gray-700 dark:text-gray-300">
            {orders.toLocaleString("th-TH")} ออเดอร์
          </span>
        </p>
        {roiMonths >= 1 && (
          <div className="mt-3 flex items-center gap-1.5 text-[13px] text-emerald-700 dark:text-emerald-300 bg-emerald-500/[0.07] rounded-lg px-3 py-2">
            <Sparkles className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
            <span>ครอบคลุมค่าบริการ Zaviago ได้ <span className="font-semibold">{roiMonths.toLocaleString("th-TH")} เดือน</span></span>
          </div>
        )}
      </div>

      {/* Trend sparkline + projection */}
      {trend.length >= 2 && (
        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="flex items-center gap-1.5">
              <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wide font-medium">6 เดือนล่าสุด</span>
              {deltaPct !== null && deltaPct !== 0 && (
                <span className={`flex items-center gap-0.5 font-medium tabular-nums ${deltaPct > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400 dark:text-gray-500"}`}>
                  {deltaPct > 0 ? <TrendingUp className="w-3 h-3" strokeWidth={2} /> : <TrendingDown className="w-3 h-3" strokeWidth={2} />}
                  {Math.abs(deltaPct)}%
                </span>
              )}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              คาดทั้งเดือนนี้ <span className="font-medium text-emerald-600 dark:text-emerald-400 tabular-nums">{money(projected, cur)}</span>
            </span>
          </div>
          <Sparkline points={trend.map((t) => t.saved)} />
          {annualized > 0 && (
            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              คาดทั้งปีตามอัตราปัจจุบัน{" "}
              <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{money(annualized, cur)}</span>
            </div>
          )}
        </div>
      )}

      {/* Quick stats */}
      <div className="mt-5 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] px-2.5 py-3">
          <div className="text-[11px] text-gray-400 dark:text-gray-500">เดือนนี้</div>
          <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 tabular-nums mt-1">{money(monthShopee, cur)}</div>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] px-2.5 py-3">
          <div className="text-[11px] text-gray-400 dark:text-gray-500">ต่อออเดอร์</div>
          <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 tabular-nums mt-1">{money(perOrder, cur)}</div>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] px-2.5 py-3">
          <div className="text-[11px] text-gray-400 dark:text-gray-500">หากผ่าน Lazada</div>
          <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100 tabular-nums mt-1">{money(savedLazada, cur)}</div>
        </div>
      </div>

      {/* Channel mix */}
      {total > 0 && (
        <div className="mt-5">
          <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2.5">สัดส่วนการขาย</div>
          <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/5">
            {directPct > 0 && <div className="bg-emerald-500" style={{ width: `${directPct}%` }} />}
            {shopeePct > 0 && <div className="bg-orange-400" style={{ width: `${shopeePct}%` }} />}
            {lazadaPct > 0 && <div className="bg-blue-400" style={{ width: `${lazadaPct}%` }} />}
            {otherPct > 0 && <div className="bg-gray-300 dark:bg-gray-600" style={{ width: `${otherPct}%` }} />}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />ขายตรง {directPct}%</span>
            {shopeePct > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" />Shopee {shopeePct}%</span>}
            {lazadaPct > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />Lazada {lazadaPct}%</span>}
          </div>
        </div>
      )}

      {/* Fees actually paid to marketplaces (orders that came from them) */}
      {feesPaid && feesPaid.total > 0 && (
        <div className="mt-5">
          <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2.5">
            ค่าธรรมเนียมที่จ่ายให้มาร์เก็ตเพลส
          </div>
          <div className="space-y-2">
            {feesPaid.shopee > 0 && (
              <div className="flex items-center justify-between text-[13px]">
                <span className="flex items-center gap-2 leading-none text-gray-600 dark:text-gray-300">
                  <ShopeeIcon className="w-4 h-4 shrink-0" />
                  <span>Shopee</span>
                  <span className="text-[11px] text-gray-400">({money(feesPaid.gmv.shopee, cur)})</span>
                </span>
                <span className="tabular-nums font-medium leading-none text-orange-600 dark:text-orange-400">−{money(feesPaid.shopee, cur)}</span>
              </div>
            )}
            {feesPaid.lazada > 0 && (
              <div className="flex items-center justify-between text-[13px]">
                <span className="flex items-center gap-2 leading-none text-gray-600 dark:text-gray-300">
                  <LazadaIcon className="w-4 h-4 shrink-0" />
                  <span>Lazada</span>
                  <span className="text-[11px] text-gray-400">({money(feesPaid.gmv.lazada, cur)})</span>
                </span>
                <span className="tabular-nums font-medium leading-none text-orange-600 dark:text-orange-400">−{money(feesPaid.lazada, cur)}</span>
              </div>
            )}
            {feesPaid.tiktok > 0 && (
              <div className="flex items-center justify-between text-[13px]">
                <span className="flex items-center gap-2 leading-none text-gray-600 dark:text-gray-300">
                  <TikTokIcon className="w-4 h-4 shrink-0" />
                  <span>TikTok</span>
                  <span className="text-[11px] text-gray-400">({money(feesPaid.gmv.tiktok, cur)})</span>
                </span>
                <span className="tabular-nums font-medium leading-none text-orange-600 dark:text-orange-400">−{money(feesPaid.tiktok, cur)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* #5 Untapped nudge — actionable link to LINE campaigns */}
      {untapped > 0 && (
        <Link
          to="/line-oa/campaigns"
          className="mt-5 flex items-start gap-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 px-3 py-2.5 transition-colors hover:bg-amber-500/[0.14]"
        >
          <ArrowUpRight className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" strokeWidth={2} />
          <p className="text-[12px] text-amber-800 dark:text-amber-200 leading-relaxed">
            ชวนลูกค้าจากมาร์เก็ตเพลสมาซื้อตรงผ่าน LINE เพื่อไม่ต้องเสียค่าธรรมเนียมส่วนนี้
            <span className="font-medium underline underline-offset-2 whitespace-nowrap"> สร้างแคมเปญ →</span>
          </p>
        </Link>
      )}

      {/* Top categories */}
      {topCats.length > 0 && (
        <div className="mt-5 pt-5 border-t border-border/50">
          <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2.5">หมวดหมู่ที่ประหยัดมากสุด</div>
          <div className="space-y-2.5">
            {topCats.slice(0, 3).map((c) => (
              <div key={c.category} className="flex items-center justify-between gap-2 text-[13px]">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 shrink-0" />
                  <span className="truncate text-gray-600 dark:text-gray-300">{c.category}</span>
                </span>
                <span className="tabular-nums text-gray-700 dark:text-gray-200 shrink-0">{money(c.saved_vs_shopee, cur)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-5 text-[11px] text-gray-400 dark:text-gray-500 flex items-start gap-1">
        <Info className="w-3 h-3 shrink-0 mt-[1px]" />
        ประมาณการจากอัตราค่าคอมมิชชันมาร์เก็ตเพลส (ไม่รวมค่าธรรมเนียมการชำระเงิน)
      </p>
    </div>
  );
}
